import type {
    KeyCodeKey,
    KeyCodeValue,
    KeyDirectionKey,
    KeyDirectionValue,
    TabIndexKey,
    TabIndexValue,
} from './utils.type';

/**
 * @memberof SharedComponents
 * @class Keyboard
 * @alias keyboard
 */

/**
 * @memberof Keyboard
 * @enum {number}
 */
const keyCodes: Record<KeyCodeKey, KeyCodeValue> = {
    BACKSPACE: 8,
    TAB: 9,
    ENTER: 13,
    ESCAPE: 27,
    SPACE: 32,
    PAGEUP: 33,
    PAGEDOWN: 34,
    HOME: 36,
    END: 35,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    DEL: 46,
};

/**
 * We use only 0 and -1, never use higher tab-indexes rather than 0.
 * @enum {string}
 */
const TabIndex: Record<TabIndexKey, TabIndexValue> = {
    Active: '0',
    Inactive: '-1',
};

/**
 * Key directions for navigation with arrows. Used for accessibility/navigation.
 * @enum {number}
 */
const keyDirections: Record<KeyDirectionKey, KeyDirectionValue> = {
    [keyCodes.LEFT]: -1,
    [keyCodes.DOWN]: -1,
    [keyCodes.RIGHT]: 1,
    [keyCodes.UP]: 1,
};

/**
 * @memberof Keyboard
 * @param {KeyboardEvent} e
 * @param {number} code
 * @returns {boolean}
 */
const isKeyPressed = (e: KeyboardEvent, code: number): boolean => {
    const keyCode = e?.which ? e?.which : e?.keyCode;
    return keyCode === code;
};

/**
 * @memberof Keyboard
 * @param {KeyboardEvent} event
 * @returns {boolean}
 */
const isTabPressed = (event: KeyboardEvent): boolean =>
    isKeyPressed(event, keyCodes.TAB);

/**
 * @memberof Keyboard
 * @param {KeyboardEvent} event
 * @returns {boolean}
 */
const isEnterPressed = (event: KeyboardEvent): boolean =>
    isKeyPressed(event, keyCodes.ENTER);

/**
 * @memberof Keyboard
 * @param {KeyboardEvent} event
 * @returns {boolean}
 */
const isEscapePressed = (event: KeyboardEvent): boolean =>
    isKeyPressed(event, keyCodes.ESCAPE);

/**
 * Returns true for modifier-key clicks (Ctrl, Meta, Shift, Alt) and non-primary
 * mouse buttons so components can let the browser handle those natively
 * (e.g. "open in new tab", "open in new window").
 * @memberof Keyboard
 * @param {MouseEvent} event
 * @returns {boolean}
 */
const isModifiedClick = (event: MouseEvent): boolean =>
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0;

export {
    keyCodes,
    keyDirections,
    TabIndex,
    isKeyPressed,
    isTabPressed,
    isEnterPressed,
    isEscapePressed,
    isModifiedClick,
};
